// =============================================================================
// NativePrelogin — pre-login landing rendered ONLY inside the Capacitor
// iOS / Android shell when the user is not authenticated.
//
// Build ID: native-prelogin-bg-logo-2026-07-03-a
//
// Why this exists:
//   The shared `<Nav>` header (web nav bar, search pill, top-right Sign Up,
//   shadow + border-b below the safe area) was leaking into the native apps
//   and making the launch screen feel like a website rendered inside a
//   webview.  Web visitors still see the full marketing landing — they need
//   the nav.  Native visitors get this dedicated, immersive layout instead.
//
// Visual contract:
//   - NO top header, NO border, NO shadow, NO background break below the
//     status-bar safe area.  The status-bar inset is consumed by an invisible
//     spacer that lets the dark page background bleed all the way to the top.
//   - The ZenScroll brand logo is rendered as a LARGE background watermark
//     centered behind the content (low opacity, pointer-events-none) so the
//     splash feels immersive and branded without competing with the CTAs.
//   - Hero copy sits above the watermark; Sign Up / Sign In CTAs anchor the
//     bottom third of the screen for one-thumb reachability.
// =============================================================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { GrainOverlay, Glow } from '@/components/landing/LandingShell';

// Console marker so QA can confirm the native pre-login bundle is loaded.
const NATIVE_PRELOGIN_BUILD_MARKER = '[ZenScroll Native Prelogin] build-native-prelogin-bg-enso-2026-07-03-b';

export default function NativePrelogin() {
  const navigate = useNavigate();

  useEffect(() => {
    // Emit the marker once per mount so a single launch produces a single log.
    console.log(NATIVE_PRELOGIN_BUILD_MARKER);
    // Hide the Emergent preview badge while we're on this screen so the
    // immersive look isn't broken on TestFlight QA builds.
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => { if (badge) badge.style.display = 'flex'; };
  }, []);

  return (
    <div
      data-testid="native-prelogin-page"
      className="relative min-h-screen bg-[#0A1514] text-white overflow-hidden flex flex-col"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Decorative layers — sit BEHIND content, no borders/separators. */}
      <GrainOverlay />
      <Glow className="-top-32 -left-32 w-[520px] h-[520px]" color="#1C4749" opacity={0.22} />
      <Glow className="bottom-0 -right-20 w-[420px] h-[420px]" color="#FFD166" opacity={0.07} />

      {/* Status-bar safe-area spacer.  Deliberately no header/divider/shadow
          — the page bg flows edge to edge so the status bar floats over
          the dark hero. */}
      <div
        aria-hidden="true"
        data-testid="native-prelogin-safe-top"
        style={{ height: 'env(safe-area-inset-top, 0px)' }}
      />

      {/* Main content — vertically centered, scrolls only if needed. */}
      <main
        className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-10"
        data-testid="native-prelogin-main"
      >
        <div className="w-full max-w-md flex flex-col items-center text-center gap-8">
          {/* Tagline — sits above the wordmark. */}
          <p
            data-testid="native-prelogin-tagline"
            className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#5EEAD4]/80"
          >
            Dream it. Create it. Share it.
          </p>

          {/* Wordmark wrapped so the enso-circle background can center on it. */}
          <div className="relative flex items-center justify-center w-full">
            {/* Background enso circle — cropped via CSS mask so ONLY the
                circle portion of zenscroll-mark-02.png shows, hiding the
                "ZenScroll" verbiage baked into the image.  Centred on the
                foreground wordmark below. */}
            <img
              src="/brand/zenscroll-mark-02.png"
              alt=""
              aria-hidden="true"
              data-testid="native-prelogin-bg-logo"
              className="pointer-events-none select-none absolute left-1/2 top-1/2 w-[900px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.32] z-0"
              draggable={false}
              style={{
                // Fade the lower portion of the artwork (which contains the
                // baked-in "ZenScroll" wordmark) to transparent so the
                // background element is JUST the circular enso brush.
                // Enso circle occupies y=29%-52% of the image; text starts
                // at ~55%.  Cut-off at 53% preserves the circle cleanly.
                WebkitMaskImage:
                  'linear-gradient(to bottom, black 0%, black 53%, transparent 60%)',
                maskImage:
                  'linear-gradient(to bottom, black 0%, black 53%, transparent 60%)',
                // The enso circle's optical centre is at 40% of the image
                // height, but the img's geometric centre is at 50%.  Shift
                // the img DOWN by 10% so the circle's centre lines up with
                // the wordmark's centre rather than sitting above it.
                translate: '0 10%',
              }}
            />

            {/* Foreground wordmark. */}
            <h1
              data-testid="native-prelogin-wordmark"
              className="relative z-10 text-5xl sm:text-6xl leading-none tracking-tight text-white"
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
            >
              ZenScroll
            </h1>
          </div>

          {/* Description — same copy as the web hero so brand voice matches. */}
          <p
            data-testid="native-prelogin-description"
            className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-sm"
          >
            Explore popular posts, daily creative prompts, new AI content, and
            positive moments from the ZenScroll community.
          </p>

          {/* Primary CTA — Sign Up. */}
          <button
            data-testid="native-prelogin-signup-btn"
            onClick={() => navigate('/auth?mode=signup')}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1C4749] text-white font-semibold rounded-full px-7 py-4 text-base hover:bg-[#2A6F72] active:bg-[#2A6F72] transition-all shadow-[0_0_28px_rgba(28,71,73,0.55)]"
          >
            Sign Up <ArrowRight className="w-4 h-4" />
          </button>

          {/* Secondary CTA — Sign In. */}
          <button
            data-testid="native-prelogin-signin-btn"
            onClick={() => navigate('/auth')}
            className="w-full inline-flex items-center justify-center bg-transparent text-white border border-white/20 rounded-full px-7 py-4 text-base hover:bg-white/5 active:bg-white/10 transition-colors"
          >
            Sign In
          </button>
        </div>
      </main>

      {/* Bottom safe-area spacer for the home-indicator on notchless iPhones
          and gesture-nav Androids — prevents the Sign In button from landing
          exactly on the swipe bar. */}
      <div
        aria-hidden="true"
        data-testid="native-prelogin-safe-bottom"
        style={{ height: 'env(safe-area-inset-bottom, 16px)' }}
      />
    </div>
  );
}
